import React, { useState } from 'react';

import { PostPlannerCard } from './PostPlannerCard';
import { SalesforceAccountSearch } from './SalesforceAccountSearch';
import * as XLSX from 'xlsx';
import { calculatePostDate } from '../utils/dateUtils';
import { uploadToSalesforce } from '../services/salesforceUploadService';
import { useAuthContext } from '../../../../contexts/AuthContext';


export const ContentPlanDisplay = ({
  contentPlan,
  practiceName,
  postSchedule,
  postsPerWeek,
  startDate,
  onContentChange,
  onRegeneratePost,
  regeneratingPosts,
  selectedSalesforceAccount,
  additionalAccounts = [],
  setAdditionalAccounts
}) => {
  const context = useAuthContext();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [uploadStatuses, setUploadStatuses] = useState({});
  const [showMultiLocation, setShowMultiLocation] = useState(false);
  const [additionalSearchValue, setAdditionalSearchValue] = useState('');

  // Sanitize text for Excel to prevent formula injection
  const sanitizeForExcel = (text) => {
    if (!text) return '';
    // Prefix with single quote if starts with dangerous characters
    if (/^[=+\-@]/.test(text)) {
      return "'" + text;
    }
    return text;
  };

  const handleDownload = () => {
    const headerRow = ['Date', 'Day', 'Week', 'Content'];
    const dataForExcel = [headerRow, ...contentPlan.flatMap((week, weekIndex) =>
      week.posts.map((post, postIndex) => {
        const postDate = calculatePostDate(startDate, weekIndex, postIndex, postSchedule);
        const formattedDate = postDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        const dayName = postDate.toLocaleDateString('en-US', { weekday: 'long' });
        return [
          formattedDate,
          dayName,
          `Week ${week.week}`,
          sanitizeForExcel(`${post.title}\n\n${post.caption}`)
        ];
      })
    )];
    const ws = XLSX.utils.aoa_to_sheet(dataForExcel);
    ws['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 100 }];
    const sheetName = `G&F - Cycle 1 - ${postSchedule}`;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const fileName = `${practiceName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-content-plan.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleAddAdditionalAccount = (account) => {
    if (!account?.Id) return;
    if (account.Id === selectedSalesforceAccount?.Id) return;
    if (additionalAccounts.some(a => a.Id === account.Id)) return;
    setAdditionalAccounts(prev => [...prev, account]);
    setAdditionalSearchValue('');
  };

  const handleRemoveAdditionalAccount = (accountId) => {
    setAdditionalAccounts(prev => prev.filter(a => a.Id !== accountId));
  };

  const handleUploadToSalesforce = async () => {
    setUploadMessage(null);
    setUploadStatuses({});

    if (!selectedSalesforceAccount || !selectedSalesforceAccount.Id) {
      setUploadMessage({
        type: 'error',
        text: 'Please select a Salesforce account before uploading.'
      });
      return;
    }

    const allAccounts = [selectedSalesforceAccount, ...additionalAccounts];
    setIsUploading(true);

    // Initialize all statuses
    const initialStatuses = {};
    allAccounts.forEach(account => {
      initialStatuses[account.Id] = { status: 'pending', message: 'Waiting...', name: account.Name };
    });
    setUploadStatuses(initialStatuses);

    let successCount = 0;
    let failCount = 0;

    for (const account of allAccounts) {
      setUploadStatuses(prev => ({
        ...prev,
        [account.Id]: { status: 'uploading', message: 'Uploading...', name: account.Name }
      }));

      try {
        const result = await uploadToSalesforce(
          account.Id,
          contentPlan,
          startDate,
          postSchedule,
          context
        );

        if (result.success) {
          successCount++;
          setUploadStatuses(prev => ({
            ...prev,
            [account.Id]: { status: 'success', message: result.message, name: account.Name }
          }));
        } else {
          failCount++;
          setUploadStatuses(prev => ({
            ...prev,
            [account.Id]: { status: 'error', message: result.message, name: account.Name }
          }));
        }
      } catch (error) {
        failCount++;
        setUploadStatuses(prev => ({
          ...prev,
          [account.Id]: {
            status: 'error',
            message: error.message || 'Upload failed',
            name: account.Name
          }
        }));
      }
    }

    setIsUploading(false);

    const total = allAccounts.length;
    if (failCount === 0) {
      setUploadMessage({
        type: 'success',
        text: `Successfully uploaded to ${successCount} account${successCount > 1 ? 's' : ''}!`
      });
    } else {
      setUploadMessage({
        type: 'error',
        text: `Uploaded to ${successCount}/${total} accounts. ${failCount} failed — see details below.`
      });
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'success': return { bg: '#d1fae5', border: '#6ee7b7', color: '#065f46' };
      case 'error': return { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b' };
      case 'uploading': return { bg: '#dbeafe', border: '#93c5fd', color: '#1e40af' };
      default: return { bg: '#f3f4f6', border: '#e5e7eb', color: '#6b7280' };
    }
  };

  const uploadButtonText = isUploading
    ? 'Uploading...'
    : additionalAccounts.length > 0
      ? `Upload to ${1 + additionalAccounts.length} Accounts`
      : 'Upload to Salesforce';

  return (
    <div style={{ marginTop: '3rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '0.75rem',
          fontFamily: 'inherit'
        }}>
          Your 12-Week Social Media Plan
        </h2>
        <p style={{
          fontSize: '0.875rem',
          color: '#6b7280',
          maxWidth: '42rem',
          margin: '0 auto',
          fontFamily: 'inherit'
        }}>
          Here is your custom content schedule for <span style={{ fontWeight: '600', color: '#156fb9' }}>{practiceName}</span>. You can edit the posts below before downloading or uploading to Salesforce.
        </p>
        <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button
            onClick={handleDownload}
            className="post-planner-action-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Download as Excel
          </button>
          <button
            onClick={handleUploadToSalesforce}
            disabled={isUploading || !selectedSalesforceAccount}
            className="post-planner-action-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" transform="rotate(180 10 10)" />
            </svg>
            {uploadButtonText}
          </button>
        </div>

        {/* Multi-Location Section */}
        {selectedSalesforceAccount && (
          <div style={{ marginTop: '1rem', maxWidth: '36rem', margin: '1rem auto 0' }}>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setShowMultiLocation(!showMultiLocation)}
              onKeyDown={(e) => { if (e.key === 'Enter') setShowMultiLocation(!showMultiLocation); }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#6b7280',
                cursor: 'pointer',
                margin: '0 auto',
                padding: '0.25rem 0.5rem',
                fontFamily: 'inherit',
                userSelect: 'none'
              }}
            >
              <span style={{
                display: 'inline-block',
                transition: 'transform 0.2s',
                transform: showMultiLocation ? 'rotate(90deg)' : 'rotate(0deg)',
                fontSize: '0.7rem'
              }}>
                &#9654;
              </span>
              {additionalAccounts.length > 0
                ? `Uploading to ${1 + additionalAccounts.length} accounts`
                : 'Add additional locations'}
            </span>

            {showMultiLocation && (
              <div style={{
                marginTop: '0.75rem',
                padding: '1rem',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fafbfc',
                textAlign: 'left'
              }}>
                <div style={{ fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  <span style={{ color: '#6b7280' }}>Primary: </span>
                  <span style={{ fontWeight: '600', color: '#111827' }}>{selectedSalesforceAccount.Name}</span>
                </div>

                <div style={{ marginBottom: '0.5rem' }}>
                  <SalesforceAccountSearch
                    value={additionalSearchValue}
                    onChange={setAdditionalSearchValue}
                    onAccountSelect={handleAddAdditionalAccount}
                    placeholder="Search for additional location..."
                  />
                </div>

                {additionalAccounts.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
                    {additionalAccounts.map(account => (
                      <div key={account.Id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '0.8rem'
                      }}>
                        <span style={{ color: '#111827', fontFamily: 'inherit' }}>{account.Name}</span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => !isUploading && handleRemoveAdditionalAccount(account.Id)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !isUploading) handleRemoveAdditionalAccount(account.Id); }}
                          style={{
                            color: isUploading ? '#9ca3af' : '#dc2626',
                            cursor: isUploading ? 'not-allowed' : 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            fontFamily: 'inherit',
                            userSelect: 'none',
                            flexShrink: 0
                          }}
                        >
                          Remove
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Per-account upload status */}
        {Object.keys(uploadStatuses).length > 0 && (
          <div style={{
            marginTop: '1rem',
            maxWidth: '36rem',
            margin: '1rem auto 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.4rem'
          }}>
            {Object.entries(uploadStatuses).map(([accountId, statusObj]) => {
              const style = getStatusStyle(statusObj.status);
              return (
                <div key={accountId} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  backgroundColor: style.bg,
                  border: `1px solid ${style.border}`,
                  color: style.color,
                  fontFamily: 'inherit'
                }}>
                  <span style={{ fontWeight: '600' }}>{statusObj.name}</span>
                  <span>{statusObj.message}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Simple upload message (shown when no per-account statuses) */}
        {uploadMessage && Object.keys(uploadStatuses).length === 0 && (
          <div
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              maxWidth: '36rem',
              margin: '1rem auto 0',
              fontSize: '0.875rem',
              backgroundColor: uploadMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
              color: uploadMessage.type === 'success' ? '#065f46' : '#991b1b',
              border: `1px solid ${uploadMessage.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
              fontFamily: 'inherit'
            }}
          >
            {uploadMessage.text}
          </div>
        )}

        {/* Summary message when per-account statuses are shown */}
        {uploadMessage && Object.keys(uploadStatuses).length > 0 && (
          <div
            style={{
              marginTop: '0.5rem',
              maxWidth: '36rem',
              margin: '0.5rem auto 0',
              fontSize: '0.8rem',
              fontWeight: '600',
              color: uploadMessage.type === 'success' ? '#065f46' : '#991b1b',
              fontFamily: 'inherit',
              textAlign: 'center'
            }}
          >
            {uploadMessage.text}
          </div>
        )}
      </div>

      <div className="post-planner-week-list">
        {contentPlan.map((weekData, weekIndex) => (
          <div key={weekData.week} className="post-planner-week">
            <h3 className="post-planner-week-title">Week {weekData.week}</h3>
            <div className="post-planner-week-grid">
              {weekData.posts.map((post, postIndex) => {
                const postDate = calculatePostDate(startDate, weekIndex, postIndex, postSchedule);
                const formattedDate = postDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                const postKey = `${weekIndex}-${postIndex}`;
                const isRegenerating = regeneratingPosts.has(postKey);

                return (
                  <PostPlannerCard
                    key={postKey}
                    post={post}
                    postDate={formattedDate}
                    weekIndex={weekIndex}
                    postIndex={postIndex}
                    onContentChange={onContentChange}
                    onRegenerate={onRegeneratePost}
                    isRegenerating={isRegenerating}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
